/**
 * CORE-1524 — напоминание «добавьте реквизиты» для участников реферальной
 * программы, когда выплаты уже включены.
 *
 * Отдельным хуком, потому что потребителей два — пункт «Финансы» в меню шапки
 * и сам экран «Финансы», — а шапка живёт на каждом экране. Ответ один на всё
 * окно: без общего кеша каждый переход между экранами стоил бы запроса
 * пропуска на бэкенд и запроса в контур ПДн.
 */
import { useEffect, useState } from 'react';
import { api } from '../services/api';
import { getPayoutDetailsStatus } from '../services/taxContour';
import { useAuth } from '../context/AuthContext';
import { useReferralPayoutEnabled, useReferralUiEnabled } from './useFeatureFlags';
import type { PayeeRecordStatus } from '../types/taxContour';

export const PAYOUT_DETAILS_REMINDER_TEXT =
    'Чтобы получать выплаты по реферальной программе, добавьте реквизиты';

/**
 * Кому напоминать. «Реквизиты есть» решает контур, а не фронт: `complete` он
 * ставит, когда заполнено всё, что нужно для выплаты (ИНН, счёт, БИК,
 * получатель). Черновик, отозванное согласие и стёртая запись — реквизитов нет.
 *
 * Статус неизвестен (контур недоступен) → не напоминаем: просить заполнить то,
 * что, возможно, уже заполнено, хуже, чем промолчать до следующего захода.
 */
export function needsPayoutDetails(input: {
    payoutsEnabled: boolean;
    enrolled: boolean;
    recordStatus: PayeeRecordStatus | null;
}): boolean {
    if (!input.payoutsEnabled || !input.enrolled) return false;
    if (input.recordStatus === null) return false;
    return input.recordStatus !== 'complete';
}

type Snapshot = { userId: string; needed: boolean };

let snapshot: Snapshot | null = null;
let inflight: { userId: string; promise: Promise<boolean> } | null = null;
const listeners = new Set<() => void>();

async function resolve(): Promise<boolean> {
    const identity = await api.getReferralIdentity().catch(() => null);
    // Вне программы контур не спрашиваем вовсе: реквизиты там не нужны, а
    // пропуск в контур — лишний запрос персонального характера.
    if (!identity?.enrolled) return false;
    // CORE-1662 — при модели баллов выплат нет, и просить реквизиты не за что.
    if (identity.rewardModel === 'points') return false;
    const status = await getPayoutDetailsStatus().catch(() => null);
    return needsPayoutDetails({
        payoutsEnabled: true,
        enrolled: true,
        recordStatus: status?.status ?? null,
    });
}

export function loadPayoutDetailsReminder(userId: string): Promise<boolean> {
    if (inflight?.userId === userId) return inflight.promise;
    const promise = resolve().then((needed) => {
        snapshot = { userId, needed };
        for (const listener of listeners) listener();
        return needed;
    }).finally(() => {
        if (inflight?.promise === promise) inflight = null;
    });
    inflight = { userId, promise };
    return promise;
}

/**
 * Перечитать после сохранения реквизитов или подключения к программе — чтобы
 * индикатор пропал (или появился) сразу, а не при следующей загрузке окна.
 */
export function refreshPayoutDetailsReminder(): void {
    const userId = snapshot?.userId ?? inflight?.userId;
    snapshot = null;
    if (userId) void loadPayoutDetailsReminder(userId);
}

export function usePayoutDetailsReminder(): boolean {
    const { user } = useAuth();
    const payoutsEnabled = useReferralPayoutEnabled();
    const referralEnabled = useReferralUiEnabled();
    const userId = user?.id ?? null;
    const active = !!userId && payoutsEnabled && referralEnabled;
    const [, rerender] = useState(0);

    useEffect(() => {
        if (!active || !userId) return;
        const onChange = () => rerender((n) => n + 1);
        listeners.add(onChange);
        if (snapshot?.userId !== userId) void loadPayoutDetailsReminder(userId);
        return () => { listeners.delete(onChange); };
    }, [active, userId]);

    return active && snapshot?.userId === userId && snapshot.needed;
}

/** Только для тестов. */
export function __resetPayoutDetailsReminder(): void {
    snapshot = null;
    inflight = null;
    listeners.clear();
}
